import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import * as calc from '../engine';
import { F, FF } from '../utils/format';
import { Card } from '../components/Card';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { Building2, Zap, TrendingUp } from '../components/icons';
import { useApp } from '../hooks/useApp';

export default function RealEstate() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const am = amor || calc.amor(s);
  const helocData = am.filter((d) => d.a >= s.hAge && d.a <= (s.maxAge || 40)).map((d) => ({
    a: d.a, heloc: Math.max(0, d.h * 0.65 - d.b), equity: d.e,
  }));

  return (
    <Stack>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {[
          { l: `Property at ${pF.a}`, v: F(pF.hv), cl: c.green },
          { l: `Equity at ${pF.a}`, v: F(pF.eq), cl: c.blue },
          { l: "Mortgage Left", v: F(pF.mBal), cl: c.orange },
          { l: "Monthly Pmt", v: FF(pF.moP), cl: c.fg },
        ].map((x, i) => (
          <Card key={x.l} c={c} delay={i * 0.04} sx={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{x.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: x.cl, marginTop: 5 }}>{x.v}</div>
          </Card>
        ))}
      </div>
      <Grid>
        <Card c={c} delay={0.08}>
          <Heading ic={Building2} c={c}>Property</Heading>
          <Slider l="Purchase Price" v={s.hPrice} fn={(v) => upM({ hPrice: v, mortAmt: Math.max(0, v - s.down) })} min={400000} max={2500000} step={25000} f="$" c={c} />
          <Slider l="Down Payment" v={s.down} fn={(v) => upM({ down: Math.min(v, s.hPrice), mortAmt: Math.max(0, s.hPrice - Math.min(v, s.hPrice)) })} min={50000} max={Math.min(600000, s.hPrice)} step={10000} f="$" c={c} />
          <Slider l="Mortgage Rate" v={s.mortRate} fn={(v) => up("mortRate", v)} min={0.02} max={0.08} step={0.001} f="%" c={c} />
          <Slider l="Appreciation" v={s.hApp} fn={(v) => up("hApp", v)} min={0.01} max={0.08} step={0.005} f="%" c={c} />
          <Slider l="Extra Principal/yr" v={s.xPrin} fn={(v) => up("xPrin", v)} min={0} max={100000} step={5000} f="$" c={c} />
          <Slider l="Purchase Age" v={s.hAge} fn={(v) => up("hAge", v)} min={23} max={35} c={c} />
        </Card>
        <Card c={c} delay={0.12}>
          <Heading ic={Zap} c={c}>HELOC Available</Heading>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={helocData}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
              <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
              <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => FF(v)} {...tt} />
              <Area type="monotone" dataKey="heloc" stroke={c.teal} fill={c.teal} fillOpacity={0.15} name="HELOC Room" />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 12, maxHeight: 160, overflowY: "auto" }}>
            {helocData.map((d) => (
              <div key={d.a} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${c.border}`, fontSize: 13 }}>
                <span style={{ color: c.muted }}>Age {d.a}</span>
                <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: c.fg }}>{F(d.heloc)}</span>
              </div>
            ))}
          </div>
        </Card>
      </Grid>
      <Card c={c} delay={0.18}>
        <Heading ic={TrendingUp} c={c}>Amortization</Heading>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={am.filter((d) => d.a <= Math.max(50, (s.maxAge || 40) + 5))}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
            <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => FF(v)} {...tt} />
            <Area type="monotone" dataKey="h" stroke={c.green} fill={c.green} fillOpacity={0.08} name="Home Value" />
            <Area type="monotone" dataKey="e" stroke={c.blue} fill={c.blue} fillOpacity={0.12} name="Equity" />
            <Area type="monotone" dataKey="b" stroke={c.red} fill={c.red} fillOpacity={0.08} name="Balance" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </Stack>
  );
}
